import { z } from 'zod';
import { getDependentFieldOptionalSchema, getDependentFieldSchema } from '../../../constants/sharedSchemas';

export const familyDetailsSchema = {
  required: z.object({
    householdMembers: z.object({
      familyDescription: z.string().min(1, 'Family description is required'),
      familySpecialCircumstances: z.string().min(1, 'Please specify'),
    }),

    typicalDay: z.object({
      typicalWeekday: z.string().min(1, 'Typical weekday description is required'),
      typicalWeekend: z.string().min(1, 'Typical weekend description is required'),
      specialWeekend: z.string().min(1, 'Special weekend description is required'),
    }),

    religion: z.object({
      religiousAffiliation: getDependentFieldSchema('other', 'Other'),
      religiousServiceFrequency: z.string().min(1, 'Please specify'),
      studentAttendPreference: z.string().min(1, 'Please specify'),
      difficultIfNotSameReligion: z.string().min(1, 'Please specify'),
    }),

    diet: z.object({
      provideStudentMealsAgree: z.boolean().refine((val) => val === true, {
        message: 'You must agree to this before continuing',
      }),
      dietaryRestrictions: getDependentFieldSchema('explanation', 'yes'),
      studentFollowRestrictions: z.string().min(1, 'Please specify'),
      comfortableStudentRestrictions: z.string().min(1, 'Please specify'),
    }),

    miscLifestyle: z.object({
      carInsurance: z.string().min(1, 'Please specify'),
      firearms: z.string().min(1, 'Please specify'),
      firearmLawAgree: z.boolean().refine((val) => val === true, {
        message: 'You must agree to this before continuing',
      }),
      familySmoke: z.string().min(1, 'Please specify'),
      familyDrink: z.string().min(1, 'Please specify'),
      chronicIllness: getDependentFieldSchema('explanation', 'yes'),
      mentalDisorder: getDependentFieldSchema('explanation', 'yes'),
      convicted: getDependentFieldSchema('explanation', 'yes'),
      contactWithChildServices: getDependentFieldSchema('explanation', 'yes'),
    }),

    financial: z.object({
      incomeRange: z.string().min(1, 'Income range is required'),
      financialStatus: z.string().min(1, 'Financial status is required'),
      publicAssistance: getDependentFieldSchema('explanation', 'yes'),
    }),
  }),
  optional: z.object({
    householdMembers: z.object({
      familyDescription: z.string().optional(),
      familySpecialCircumstances: z.string().optional(),
    }),

    typicalDay: z.object({
      typicalWeekday: z.string().optional(),
      typicalWeekend: z.string().optional(),
      specialWeekend: z.string().optional(),
    }),

    religion: z.object({
      religiousAffiliation: getDependentFieldOptionalSchema('other'),
      religiousServiceFrequency: z.string().optional(),
      studentAttendPreference: z.string().optional(),
      difficultIfNotSameReligion: z.string().optional(),
    }),

    diet: z.object({
      provideStudentMealsAgree: z.boolean().optional(),
      dietaryRestrictions: getDependentFieldOptionalSchema('explanation'),
      studentFollowRestrictions: z.string().optional(),
      comfortableStudentRestrictions: z.string().optional(),
    }),

    miscLifestyle: z.object({
      carInsurance: z.string().optional(),
      firearms: z.string().optional(),
      firearmLawAgree: z.boolean().optional(),
      familySmoke: z.string().optional(),
      familyDrink: z.string().optional(),
      chronicIllness: getDependentFieldOptionalSchema('explanation'),
      mentalDisorder: getDependentFieldOptionalSchema('explanation'),
      convicted: getDependentFieldOptionalSchema('explanation'),
      contactWithChildServices: getDependentFieldOptionalSchema('explanation'),
    }),

    financial: z.object({
      incomeRange: z.string().optional(),
      financialStatus: z.string().optional(),
      publicAssistance: getDependentFieldOptionalSchema('explanation'),
    }),
  }),
};

export type FamilyDetailsFormValues = z.infer<(typeof familyDetailsSchema)['required']> | z.infer<(typeof familyDetailsSchema)['optional']>;

export const defaultValues: FamilyDetailsFormValues = {
  householdMembers: {
    familyDescription: '',
    familySpecialCircumstances: '',
  },

  typicalDay: {
    typicalWeekday: '',
    typicalWeekend: '',
    specialWeekend: '',
  },

  religion: {
    religiousAffiliation: { value: '', other: '' },
    religiousServiceFrequency: '',
    studentAttendPreference: '',
    difficultIfNotSameReligion: '',
  },

  diet: {
    provideStudentMealsAgree: false,
    dietaryRestrictions: { value: '', explanation: '' },
    studentFollowRestrictions: '',
    comfortableStudentRestrictions: '',
  },

  miscLifestyle: {
    carInsurance: '',
    firearms: '',
    firearmLawAgree: false,
    familySmoke: '',
    familyDrink: '',
    chronicIllness: { value: '', explanation: '' },
    mentalDisorder: { value: '', explanation: '' },
    convicted: { value: '', explanation: '' },
    contactWithChildServices: { value: '', explanation: '' },
  },

  financial: {
    incomeRange: '',
    financialStatus: '',
    publicAssistance: { value: '', explanation: '' },
  },
};
